import {
  argbFromHex,
  DislikeAnalyzer,
  DynamicScheme,
  Hct,
  hexFromArgb,
  TemperatureCache,
  TonalPalette,
} from '@material/material-color-utilities';

export interface ColorPalettes {
  primary: TonalPalette;
  secondary: TonalPalette;
  tertiary: TonalPalette;
  neutral: TonalPalette;
  neutralVariant: TonalPalette;
  error: TonalPalette;
}

export interface MaterialColorPalettes {
  primary: {[key: string]: string};
  secondary: {[key: string]: string};
  tertiary: {[key: string]: string};
  neutral?: {[key: string]: string};
  neutralVariant?: {[key: string]: string};
  error?: {[key: string]: string};
}

const HUE_TONES = [0, 10, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 95, 98, 99, 100];

const NEUTRAL_HUES = new Map<number, {prev: number; next: number}>([
  [4, {prev: 0, next: 10}],
  [6, {prev: 0, next: 10}],
  [12, {prev: 10, next: 20}],
  [17, {prev: 10, next: 20}],
  [22, {prev: 20, next: 25}],
  [24, {prev: 20, next: 25}],
  [87, {prev: 80, next: 90}],
  [92, {prev: 90, next: 95}],
  [94, {prev: 90, next: 95}],
  [96, {prev: 95, next: 98}],
]);

const NEUTRAL_HUE_TONES = [...HUE_TONES, ...NEUTRAL_HUES.keys()];

export function getMaterialDynamicScheme(
  primaryPalette: TonalPalette,
  secondaryPalette: TonalPalette,
  tertiaryPalette: TonalPalette,
  neutralPalette: TonalPalette,
  neutralVariantPalette: TonalPalette,
  isDark: boolean,
  contrastLevel: number,
): DynamicScheme {
  return new DynamicScheme({
    sourceColorArgb: primaryPalette.keyColor.toInt(),
    variant: 6,
    contrastLevel: contrastLevel,
    isDark: isDark,
    primaryPalette: primaryPalette,
    secondaryPalette: secondaryPalette,
    tertiaryPalette: tertiaryPalette,
    neutralPalette: neutralPalette,
    neutralVariantPalette: neutralVariantPalette,
  });
}

export function getColorPalettes(
  primaryColor: string,
  secondaryColor?: string,
  tertiaryColor?: string,
  neutralColor?: string,
  neutralVariantColor?: string,
  errorColor?: string,
): ColorPalettes {
  const primaryColorHct = getHctFromHex(primaryColor);
  const primaryPalette = TonalPalette.fromHct(primaryColorHct);

  let secondaryPalette;
  if (secondaryColor) {
    secondaryPalette = TonalPalette.fromHct(getHctFromHex(secondaryColor));
  } else {
    secondaryPalette = TonalPalette.fromHueAndChroma(
      primaryColorHct.hue,
      Math.max(primaryColorHct.chroma - 32.0, primaryColorHct.chroma * 0.5),
    );
  }

  let tertiaryPalette;
  if (tertiaryColor) {
    tertiaryPalette = TonalPalette.fromHct(getHctFromHex(tertiaryColor));
  } else {
    tertiaryPalette = TonalPalette.fromInt(
      DislikeAnalyzer.fixIfDisliked(
        new TemperatureCache(primaryColorHct).analogous(3, 6)[2],
      ).toInt(),
    );
  }

  let neutralPalette;
  if (neutralColor) {
    neutralPalette = TonalPalette.fromHct(getHctFromHex(neutralColor));
  } else {
    neutralPalette = TonalPalette.fromHueAndChroma(
      primaryColorHct.hue,
      primaryColorHct.chroma / 8.0,
    );
  }

  let neutralVariantPalette;
  if (neutralVariantColor) {
    neutralVariantPalette = TonalPalette.fromHct(getHctFromHex(neutralVariantColor));
  } else {
    neutralVariantPalette = TonalPalette.fromHueAndChroma(
      primaryColorHct.hue,
      primaryColorHct.chroma / 8.0 + 4.0,
    );
  }

  let errorPalette;
  if (errorColor) {
    errorPalette = TonalPalette.fromHct(getHctFromHex(errorColor));
  } else {
    errorPalette = getMaterialDynamicScheme(
      primaryPalette,
      secondaryPalette,
      tertiaryPalette,
      neutralPalette,
      neutralVariantPalette,
      false,
      0,
    ).errorPalette;
  }

  return {
    primary: primaryPalette,
    secondary: secondaryPalette,
    tertiary: tertiaryPalette,
    neutral: neutralPalette,
    neutralVariant: neutralVariantPalette,
    error: errorPalette,
  };
}

export function getColorPalettesObject(colorPalettes: ColorPalettes): MaterialColorPalettes {
  const matColorPalettes: MaterialColorPalettes = {
    primary: {},
    secondary: {},
    tertiary: {},
  };
  for (const [variant, palette] of Object.entries(colorPalettes)) {
    const paletteKey = variant.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    const tones = paletteKey === 'neutral' ? NEUTRAL_HUE_TONES : HUE_TONES;
    matColorPalettes[paletteKey as keyof MaterialColorPalettes] = tones.reduce(
      (acc, tone) => {
        const color = hexFromArgb(palette.tone(tone));
        acc[tone] = color;
        return acc;
      },
      {} as {[key: string]: string},
    );
  }
  return matColorPalettes;
}

export function getHctFromHex(color: string): Hct {
  try {
    return Hct.fromInt(argbFromHex(color));
  } catch (e) {
    throw new Error(
      'Cannot parse the specified color ' +
        color +
        '. Please verify it is a hex color (ex. #ffffff or ffffff).',
    );
  }
}

/* INFO: OLD WOKRING FUNCITONS */
export function generatePalette(baseColor: string): any {
  return {
    0: '#000000',
    10: darken(baseColor, 45),
    20: darken(baseColor, 35),
    25: darken(baseColor, 30),
    30: darken(baseColor, 25),
    35: darken(baseColor, 20),
    40: darken(baseColor, 15),
    50: baseColor,
    60: lighten(baseColor, 10),
    70: lighten(baseColor, 20),
    80: lighten(baseColor, 30),
    90: lighten(baseColor, 40),
    95: lighten(baseColor, 50),
    99: lighten(baseColor, 80),
    100: '#FFFFFF',
    contrast: '#FFFFFF',
  };
}

export function lighten(hex: string, percentage: number): string {
  let {r, g, b} = hexToRgb(hex);
  // eslint-disable-next-line prefer-const
  let {h, s, l} = rgbToHsl(r, g, b);

  l = Math.min(1, l + percentage * 0.01); // Increase lightness
  ({r, g, b} = hslToRgb(h, s, l));

  return rgbToHex(r, g, b);
}

export function darken(hex: string, percentage: number): string {
  let {r, g, b} = hexToRgb(hex);
  // eslint-disable-next-line prefer-const
  let {h, s, l} = rgbToHsl(r, g, b);

  l = Math.max(0.1, l - percentage * 0.01);

  ({r, g, b} = hslToRgb(h, s, l));

  return rgbToHex(r, g, b);
}

export function hexToRgb(hex: string): {r: number; g: number; b: number} {
  hex = hex.replace(/^#/, '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return {r, g, b};
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
}

export function rgbToHsl(r: number, g: number, b: number): {h: number; s: number; l: number} {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);

  let h = 0,
    s = 0,
    // eslint-disable-next-line prefer-const
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {h: h * 360, s, l};
}

export function hslToRgb(h: number, s: number, l: number): {r: number; g: number; b: number} {
  let r, g, b;

  function hueToRgb(p: number, q: number, t: number) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  h /= 360;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgb(p, q, h + 1 / 3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1 / 3);
  }

  return {r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255)};
}
